import React, { useState, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  List,
  ListItem,
  ListItemAvatar,
  Avatar,
  ListItemText,
  Divider,
  Button,
  TextField,
  Paper,
  Chip,
  IconButton,
  Collapse,
  Card,
  CardContent,
  Grid,
  Skeleton,
  Alert,
  Tooltip,
  Badge,
  alpha,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Close,
  PersonAdd,
  Share,
  ExpandMore,
  ExpandLess,
  AttachFile,
  Assignment,
  Schedule,
  CheckCircle,
  Error as ErrorIcon,
  Refresh
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import API from '../../API';
import BlitzChatRulesSection from './blitzrules';

/* ------------------------------------------------------------------ */
/*  Helper Functions                                                  */
/* ------------------------------------------------------------------ */
function formatLastOnline(lastOnline) {
  if (!lastOnline) return 'Never logged in';
  
  const now = new Date();
  const dt = new Date(lastOnline);
  const diffMs = now - dt;
  
  if (diffMs < 0) return 'Unknown';
  
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'Online now';
  if (diffMins < 5) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
  
  return dt.toLocaleDateString();
}

function getStatusColor(status) {
  switch (status?.toLowerCase()) {
    case 'completed':
    case 'paid':
    case 'accepted':
      return 'success';
    case 'pending':
    case 'sent':
      return 'warning';
    case 'rejected':
    case 'cancelled':
      return 'error';
    default:
      return 'default';
  }
}

/* ------------------------------------------------------------------ */
/*  Enhanced ParticipantsPanel Component                            */
/* ------------------------------------------------------------------ */
export default function ParticipantsPanel({ conversationId, onClose }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const queryClient = useQueryClient();
  
  // State management
  const [inviteEmails, setInviteEmails] = useState('');
  const [expandedSections, setExpandedSections] = useState({
    participants: true,
    attachments: false,
    deliverables: false,
  });

  // Toggle section expansion
  const toggleSection = useCallback((section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  }, []);

  /* ------------------------------------------------------------------ */
  /*  Optimized Data Fetching                                          */
  /* ------------------------------------------------------------------ */
  
  // Fetch conversation metadata (lightweight)
  const {
    data: metadata,
    isLoading: metadataLoading,
    error: metadataError,
    refetch: refetchMetadata
  } = useQuery(
    ['conversation', 'metadata', conversationId],
    () => API.conversations.getMetadata(conversationId),
    { 
      enabled: !!conversationId,
      staleTime: 1000 * 60 * 5, // 5 minutes
    }
  );

  // Fetch detailed messages only when needed for attachments/deliverables
  const {
    data: conversationData,
    isLoading: messagesLoading,
    error: messagesError,
    refetch: refetchMessages
  } = useQuery(
    ['messages', conversationId],
    () => API.conversations.getMessages(conversationId),
    { 
      enabled: !!conversationId && (expandedSections.attachments || expandedSections.deliverables),
      staleTime: 1000 * 60 * 2, // 2 minutes
    }
  );

  /* ------------------------------------------------------------------ */
  /*  Processed Data                                                   */
  /* ------------------------------------------------------------------ */
  const participants = useMemo(() => {
    return conversationData?.participants || metadata?.participants || [];
  }, [conversationData?.participants, metadata?.participants]);

  const creator = useMemo(() => 
    participants.find(p => p.is_creator), 
    [participants]
  );

  const brandUsers = useMemo(() => 
    participants.filter(p => !p.is_creator), 
    [participants]
  );

  // Optimized attachment processing
  const allAttachments = useMemo(() => {
    if (!conversationData?.messages) return [];
    
    const attachments = [];
    conversationData.messages.forEach((msg) => {
      if (msg.attachments) {
        attachments.push({
          ...msg.attachments,
          messageId: msg.id,
          timestamp: msg.created_at
        });
      }
    });
    return attachments.slice(0, 20); // Limit to prevent performance issues
  }, [conversationData?.messages]);

  // Optimized deliverable processing
  const deliverables = useMemo(() => {
    if (!conversationData?.messages) return [];
    
    return conversationData.messages
      .filter(msg => !!msg.deliverable)
      .map(msg => ({
        ...msg.deliverable,
        messageId: msg.id,
        timestamp: msg.created_at
      }))
      .slice(0, 10); // Limit to prevent performance issues
  }, [conversationData?.messages]);

  /* ------------------------------------------------------------------ */
  /*  Mutations                                                        */
  /* ------------------------------------------------------------------ */
  const inviteByEmailMutation = useMutation(
    (payload) => API.conversations.inviteByEmail(conversationId, payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['conversation', 'metadata', conversationId]);
        queryClient.invalidateQueries(['messages', conversationId]);
        setInviteEmails('');
      },
      onError: (err) => {
        console.error('Error inviting participants:', err);
      },
    }
  );

  /* ------------------------------------------------------------------ */
  /*  Event Handlers                                                   */
  /* ------------------------------------------------------------------ */
  const handleInviteClick = useCallback(() => {
    const trimmed = inviteEmails.trim();
    if (!trimmed) return;
    
    const emailsArray = trimmed
      .split(',')
      .map(email => email.trim())
      .filter(Boolean);
    
    inviteByEmailMutation.mutate({ emails: emailsArray });
  }, [inviteEmails, inviteByEmailMutation]);

  const handleShare = useCallback(() => {
    if (navigator.share) {
      navigator.share({
        title: 'Join Conversation',
        url: `${window.location.origin}/join/${conversationId}`
      });
    } else {
      // Fallback to clipboard
      navigator.clipboard.writeText(`${window.location.origin}/join/${conversationId}`);
    }
  }, [conversationId]);

  /* ------------------------------------------------------------------ */
  /*  Render Components                                                */
  /* ------------------------------------------------------------------ */
  const renderSectionHeader = (title, count, icon, sectionKey) => (
    <Box
      onClick={() => toggleSection(sectionKey)}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        cursor: 'pointer',
        py: 1,
        px: 2,
        borderRadius: 1,
        '&:hover': {
          bgcolor: alpha(theme.palette.primary.main, 0.04),
        },
        transition: 'background-color 0.2s',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {icon}
        <Typography variant="subtitle1" fontWeight="600">
          {title}
        </Typography>
        {count !== undefined && (
          <Chip 
            label={count} 
            size="small" 
            sx={{ 
              height: 20, 
              fontSize: '0.75rem',
              fontWeight: 600,
            }} 
          />
        )}
      </Box>
      {expandedSections[sectionKey] ? <ExpandLess /> : <ExpandMore />}
    </Box>
  );

  const renderParticipant = (participant, isCreator = false) => (
    <ListItem
      key={participant.id}
      sx={{
        py: 1.5,
        px: 2,
        borderRadius: 2,
        '&:hover': {
          bgcolor: alpha(theme.palette.action.hover, 0.5),
        },
      }}
    >
      <ListItemAvatar>
        <Badge
          overlap="circular"
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          badgeContent={
            participant.last_online && 
            new Date() - new Date(participant.last_online) < 300000 ? (
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  bgcolor: 'success.main',
                  border: '2px solid white',
                }}
              />
            ) : null
          }
        >
          <Avatar 
            src={participant.pfp || ''} 
            sx={{ 
              width: 48, 
              height: 48,
              border: `2px solid ${isCreator ? theme.palette.primary.main : theme.palette.grey[300]}`,
            }}
          >
            {participant.username?.charAt(0)?.toUpperCase() || '?'}
          </Avatar>
        </Badge>
      </ListItemAvatar>
      <ListItemText
        primary={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="subtitle2" fontWeight="600">
              {participant.username || 'Unknown User'}
            </Typography>
            {isCreator && (
              <Chip 
                label="Creator" 
                size="small" 
                color="primary" 
                sx={{ height: 20, fontSize: '0.7rem' }}
              />
            )}
          </Box>
        }
        secondary={
          <Typography variant="caption" color="text.secondary">
            Last online: {formatLastOnline(participant.last_online)}
          </Typography>
        }
      />
    </ListItem>
  );

  const renderAttachment = (attachment, index) => {
    const isImage = attachment.file_type === 'image' ||
      (attachment.filename || '').match(/\.(png|jpe?g|gif|webp)$/i);

    return (
      <Card key={index} sx={{ mb: 1 }}>
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {isImage ? (
              <Avatar
                src={attachment.file_url}
                variant="rounded"
                sx={{ width: 48, height: 48 }}
              >
                <AttachFile />
              </Avatar>
            ) : (
              <Avatar
                variant="rounded"
                sx={{ 
                  width: 48, 
                  height: 48, 
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  color: 'primary.main'
                }}
              >
                <AttachFile />
              </Avatar>
            )}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" fontWeight="600" noWrap>
                {attachment.filename || attachment.attachment_type || 'Unknown file'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {attachment.file_type && `${attachment.file_type.toUpperCase()} • `}
                {new Date(attachment.timestamp).toLocaleDateString()}
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>
    );
  };

  const renderDeliverable = (deliverable, index) => (
    <Card key={deliverable.id || index} sx={{ mb: 1 }}>
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar
            sx={{ 
              bgcolor: alpha(theme.palette.secondary.main, 0.1),
              color: 'secondary.main'
            }}
          >
            <Assignment />
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" fontWeight="600" noWrap>
              {deliverable.name || `Deliverable #${deliverable.id || index + 1}`}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
              <Chip
                label={deliverable.status || 'unknown'}
                size="small"
                color={getStatusColor(deliverable.status)}
                sx={{ height: 18, fontSize: '0.7rem' }}
              />
              <Typography variant="caption" color="text.secondary">
                {new Date(deliverable.timestamp).toLocaleDateString()}
              </Typography>
            </Box>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  /* ------------------------------------------------------------------ */
  /*  Main Render                                                      */
  /* ------------------------------------------------------------------ */
  return (
    <Paper
      sx={{
        width: { xs: '100vw', sm: 450 },
        height: { xs: '85vh', sm: '100%' },
        maxHeight: { xs: '85vh', sm: '100%' },
        display: 'flex',
        flexDirection: 'column',
        borderRadius: { xs: 0, sm: 2 },
        overflow: 'hidden',
      }}
    >
      {/* Enhanced Header */}
      <Box
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: `1px solid ${theme.palette.divider}`,
          bgcolor: alpha(theme.palette.primary.main, 0.02),
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight="600">
            Conversation Details
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {participants.length} participant{participants.length !== 1 ? 's' : ''}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Refresh">
            <IconButton 
              onClick={() => {
                refetchMetadata();
                refetchMessages();
              }}
              size="small"
            >
              <Refresh />
            </IconButton>
          </Tooltip>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </Box>

      {/* Scrollable Content */}
      <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
          {/* Blitz Chat Rules */}
          <BlitzChatRulesSection />

          {/* Loading State */}
          {metadataLoading && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              {[...Array(3)].map((_, i) => (
                <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Skeleton variant="circular" width={48} height={48} />
                  <Box sx={{ flex: 1 }}>
                    <Skeleton variant="text" width="60%" />
                    <Skeleton variant="text" width="40%" />
                  </Box>
                </Box>
              ))}
            </Box>
          )}

          {/* Error State */}
          {(metadataError || messagesError) && (
            <Alert 
              severity="error" 
              sx={{ mt: 2 }}
              action={
                <Button size="small" onClick={() => {
                  refetchMetadata();
                  refetchMessages();
                }}>
                  Retry
                </Button>
              }
            >
              Failed to load conversation details
            </Alert>
          )}

          {/* Participants Section */}
          {!metadataLoading && participants.length > 0 && (
            <Paper sx={{ mt: 2, overflow: 'hidden' }} variant="outlined">
              {renderSectionHeader(
                'Participants',
                participants.length,
                <PersonAdd color="primary" />,
                'participants'
              )}
              <Collapse in={expandedSections.participants}>
                <List sx={{ p: 0 }}>
                  {creator && renderParticipant(creator, true)}
                  {creator && brandUsers.length > 0 && <Divider sx={{ mx: 2 }} />}
                  {brandUsers.map(participant => renderParticipant(participant, false))}
                </List>
              </Collapse>
            </Paper>
          )}

          {/* Attachments Section */}
          <Paper sx={{ mt: 2, overflow: 'hidden' }} variant="outlined">
            {renderSectionHeader(
              'Attachments',
              allAttachments.length,
              <AttachFile color="action" />,
              'attachments'
            )}
            <Collapse in={expandedSections.attachments}>
              <Box sx={{ p: 2 }}>
                {messagesLoading && expandedSections.attachments ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                    <CircularProgress size={32} />
                  </Box>
                ) : allAttachments.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>
                    No attachments yet
                  </Typography>
                ) : (
                  allAttachments.map(renderAttachment)
                )}
              </Box>
            </Collapse>
          </Paper>

          {/* Deliverables Section */}
          <Paper sx={{ mt: 2, overflow: 'hidden' }} variant="outlined">
            {renderSectionHeader(
              'Deliverables',
              deliverables.length,
              <Assignment color="action" />,
              'deliverables'
            )}
            <Collapse in={expandedSections.deliverables}>
              <Box sx={{ p: 2 }}>
                {messagesLoading && expandedSections.deliverables ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                    <CircularProgress size={32} />
                  </Box>
                ) : deliverables.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>
                    No deliverables yet
                  </Typography>
                ) : (
                  deliverables.map(renderDeliverable)
                )}
              </Box>
            </Collapse>
          </Paper>
        </Box>

        {/* Bottom Actions */}
        <Box sx={{ p: 2, borderTop: `1px solid ${theme.palette.divider}`, bgcolor: 'background.paper' }}>
          {/* Quick Invite Button */}
          <Button
            variant="outlined"
            fullWidth
            onClick={handleShare}
            startIcon={<Share />}
            sx={{ mb: 2 }}
          >
            Share Invite Link
          </Button>

          {/* Email Invite Section */}
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            Invite by email
          </Typography>
          <TextField
            placeholder="john@example.com, jane@example.com"
            variant="outlined"
            fullWidth
            size="small"
            value={inviteEmails}
            onChange={(e) => setInviteEmails(e.target.value)}
            sx={{ mb: 1 }}
          />
          <Button
            variant="contained"
            fullWidth
            onClick={handleInviteClick}
            disabled={inviteByEmailMutation.isLoading || !inviteEmails.trim()}
            startIcon={inviteByEmailMutation.isLoading ? <CircularProgress size={16} /> : <PersonAdd />}
          >
            {inviteByEmailMutation.isLoading ? 'Sending...' : 'Send Invites'}
          </Button>
        </Box>
      </Box>
    </Paper>
  );
}